import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  Contact,
  Query,
  QueryContactArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { Tooltip } from 'src/components/general/display/tooltip';
import { PhoneIcon } from 'src/components/pages/conversations/components/contact-interactions/shared';
import {
  OpenLegacyRemoteModalLink,
  openRemoteModalWrapper,
} from 'src/components/pages/legacy/components/open-legacy-remote-modal-link';
import { GET_CONTACT_BLOCKED } from 'src/hooks/gql/use-refresh-contact';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useUrls } from 'src/hooks/use-urls';
import { publishEvent } from 'src/utils/events/pub-sub';
import { usePhoneNumberValidationUtils } from 'src/utils/phone-number';

const StyledOpenLegacyModalLink = styled(OpenLegacyRemoteModalLink)`
  display: flex;
`;

const StyledLink = styled(Link)`
  display: flex;
`;

type OpenLegacyCallModalIconProps = {
  contact: Contact;
  'data-lgg-id'?: string;
};

export const OpenLegacyCallModalIcon = memo<OpenLegacyCallModalIconProps>(
  ({ contact, 'data-lgg-id': dataLggId, ...rest }) => {
    const { getFeatureFlag } = useAuthorization();
    const { id: contactId } = contact;
    const { getContactCallUrl, getLegacyContactCallUrl } = useUrls();
    const { t } = useTranslation(['contacts']);
    const { hasOnlyUnsupportedRegionCallNumbers } = usePhoneNumberValidationUtils();
    const hasFFUxRedesignV2_5 = getFeatureFlag('ux-redesign-v2.5');

    const onlyUnsupportedRegionNumbers = hasOnlyUnsupportedRegionCallNumbers(contact);
    const hasNoNumbers = !contact.primaryPhone && !contact.secondaryPhone;

    const isDisabled = useMemo(() => {
      if (contact.isBlocked) {
        return false;
      }
      return hasNoNumbers || onlyUnsupportedRegionNumbers;
    }, [contact.isBlocked, hasNoNumbers, onlyUnsupportedRegionNumbers]);

    const tooltipText = useMemo(() => {
      return match({
        isBlocked: contact.isBlocked,
        hasNoNumbers,
        onlyUnsupportedRegionNumbers,
      })
        .with({ isBlocked: true }, () => t('contacts:contactActionsTooltips.callContact'))
        .with({ hasNoNumbers: true }, () =>
          t('contacts:contactActionsTooltips.noNumberToCall'),
        )
        .with({ onlyUnsupportedRegionNumbers: true }, () =>
          t('contacts:contactActionsTooltips.numberNotFromSupportedRegion'),
        )
        .otherwise(() => t('contacts:contactActionsTooltips.callContact'));
    }, [contact.isBlocked, hasNoNumbers, onlyUnsupportedRegionNumbers, t]);

    if (isDisabled) {
      return (
        <Tooltip title={tooltipText} align={{ offset: [0, 5] }}>
          <span>
            <PhoneIcon
              type="phone"
              style={{
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
              data-lgg-id={dataLggId}
              {...rest}
            />
          </span>
        </Tooltip>
      );
    }

    if (contact.isBlocked) {
      return (
        <PhoneIcon
          onClick={() => publishEvent('SHOW_UNBLOCK_CONTACT_MODAL', { contact })}
          type="phone"
          data-lgg-id={dataLggId}
          {...rest}
        />
      );
    }

    if (hasFFUxRedesignV2_5) {
      return (
        <StyledLink {...rest} to={getContactCallUrl(contactId)} data-lgg-id={dataLggId}>
          <PhoneIcon type="phone" />
        </StyledLink>
      );
    }

    return (
      <StyledOpenLegacyModalLink
        {...rest}
        url={getLegacyContactCallUrl(contactId)}
        data-lgg-id={dataLggId}
      >
        <PhoneIcon type="phone" />
      </StyledOpenLegacyModalLink>
    );
  },
);

export const useOpenCallContactModal = () => {
  const { getFeatureFlag } = useAuthorization();
  const history = useHistory();
  const { getContactCallUrl, getLegacyContactCallUrl } = useUrls();
  const handleGraphQLError = useHandleGraphQLError();
  const [fetchContact] = useLazyQuery<Pick<Query, 'contact'>, QueryContactArgs>(
    GET_CONTACT_BLOCKED,
    { onError: handleGraphQLError },
  );

  const hasFFUxRedesignV2_5 = getFeatureFlag('ux-redesign-v2.5');

  return async (contactId: number) => {
    const { data } = await fetchContact({
      variables: { id: contactId },
    });

    const contact = data?.contact;

    if (contact?.isBlocked) {
      publishEvent('SHOW_UNBLOCK_CONTACT_MODAL', { contact });
      return;
    }

    if (hasFFUxRedesignV2_5) {
      history.push(getContactCallUrl(contactId));
    } else {
      openRemoteModalWrapper(getLegacyContactCallUrl(contactId));
    }
  };
};
